import React, { useState, useMemo } from 'react';
import { useFacilityAvailability } from '../../hooks/useFacilityAvailability';
import { getFacilityById, FACILITY_TYPES } from '../../hooks/facilityConfig';
import './GuestRoomCalendarView.css';

const GuestRoomCalendarView = ({ facilityId }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  
  const facility = getFacilityById(facilityId);
  
  // Fetch 90 days of data for guest rooms
  const { data, loading, error, refetch } = useFacilityAvailability({
    facilityIds: [facilityId],
    days: 90,
    autoRefetch: true,
    refetchInterval: 5 * 60 * 1000 // 5 minutes
  });

  // Get calendar data for the selected month
  const calendarData = useMemo(() => {
    if (!data || !data.facilities || !data.facilities[facilityId]) {
      return null;
    }

    const facilityData = data.facilities[facilityId];
    if (facilityData.error || !facilityData.days) {
      return null;
    }

    // Create a map of date to availability status
    const availabilityMap = {};
    facilityData.days.forEach(day => {
      const dateStr = day.date.replace(/\//g, '-');
      availabilityMap[dateStr] = day.available || day.status === 'available';
    });

    return availabilityMap;
  }, [data, facilityId]);

  // Generate calendar grid
  const generateCalendarGrid = () => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    
    const grid = [];
    
    // Add padding for start of month
    for (let i = 0; i < startPadding; i++) {
      grid.push(null);
    }
    
    // Add days of month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(year, month, day);
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      grid.push({
        date,
        dateStr,
        day,
        isToday: isToday(date),
        isPast: isPastDate(date),
        isAvailable: calendarData ? calendarData[dateStr] : null
      });
    }
    
    return grid;
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is in the past
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  // Navigate months
  const goToPreviousMonth = () => {
    setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setSelectedMonth(new Date());
  };

  // Format month display
  const formatMonthYear = (date) => {
    return date.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long' });
  };

  if (loading) {
    return (
      <div className="guest-room-calendar loading">
        <div className="loading-spinner"></div>
        <p>空室状況を読み込み中...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="guest-room-calendar error">
        <p className="error-message">エラーが発生しました</p>
        <button onClick={refetch}>再読み込み</button>
      </div>
    );
  }

  return (
    <div className="guest-room-calendar">
      <div className="calendar-header">
        <h3 className="facility-name">
          <span className="facility-icon">{facility.icon}</span>
          {facility.name}
        </h3>
        <div className="month-navigation">
          <button onClick={goToPreviousMonth} className="nav-button">‹</button>
          <span className="current-month">{formatMonthYear(selectedMonth)}</span>
          <button onClick={goToNextMonth} className="nav-button">›</button>
          <button onClick={goToToday} className="today-button">今日</button>
        </div>
      </div>

      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color available"></span>
          <span>空室</span>
        </div>
        <div className="legend-item">
          <span className="legend-color unavailable"></span>
          <span>満室</span>
        </div>
        <div className="legend-item">
          <span className="legend-color past"></span>
          <span>過去日</span>
        </div>
      </div>

      <div className="calendar-grid">
        <div className="weekday-headers">
          {['日', '月', '火', '水', '木', '金', '土'].map(day => (
            <div key={day} className="weekday-header">{day}</div>
          ))}
        </div>
        
        <div className="calendar-days">
          {generateCalendarGrid().map((dayData, index) => {
            if (!dayData) {
              return <div key={`empty-${index}`} className="calendar-day empty"></div>;
            }

            const dayClasses = [
              'calendar-day',
              dayData.isToday && 'today',
              dayData.isPast && 'past',
              dayData.isAvailable === true && 'available',
              dayData.isAvailable === false && 'unavailable',
              dayData.isAvailable === null && 'no-data'
            ].filter(Boolean).join(' ');

            return (
              <div key={dayData.dateStr} className={dayClasses}>
                <div className="day-number">{dayData.day}</div>
                {!dayData.isPast && dayData.isAvailable !== null && (
                  <div className="availability-indicator">
                    {dayData.isAvailable ? '〇' : '✗'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="calendar-footer">
        <p className="check-times">
          チェックイン: {facility.checkInTime} / チェックアウト: {facility.checkOutTime}
        </p>
        <p className="last-updated">
          最終更新: {data && data.lastUpdated ? new Date(data.lastUpdated).toLocaleString('ja-JP') : '---'}
        </p>
      </div>
    </div>
  );
};

export default GuestRoomCalendarView;